import { NotificationToastDesignTokens } from '../types/notification';
import type { WeaveTheme } from '../types/weaveTypes';

type StatusColor = { value: string };

type ThemeArg = {
  spacing: { S: number; M: number; XXXS: number };
  borderRadius: { medium: number };
  shadows: { lowElevation: string };
  tokens: {
    colors: {
      background: {
        primary: StatusColor;
        information: { subtler: StatusColor };
        success: { subtler: StatusColor };
        warning: { subtler: StatusColor };
        error: { subtler: StatusColor };
      };
      border: {
        information: StatusColor;
        success: StatusColor;
        warning: StatusColor;
        error: StatusColor;
      };
      icon: {
        secondary: StatusColor;
        information: StatusColor;
        success: StatusColor;
        warning: StatusColor;
        error: StatusColor;
      };
    };
  };
};

export function getNotificationTokensAlloy(
  theme: ThemeArg,
): NotificationToastDesignTokens {
  const { spacing, borderRadius, shadows, tokens } = theme;
  const { background, border, icon } = tokens.colors;

  return {
    'background-color': background.primary.value,
    'border-radius': `${borderRadius.medium}px`,
    'border-left-width': '4px',
    'border-left-style': 'solid',
    'box-shadow': shadows.lowElevation,
    'padding-top': `${spacing.S}px`,
    'padding-right': `${spacing.M}px`,
    'padding-bottom': `${spacing.S}px`,
    'padding-left': `${spacing.M}px`,
    width: '320px',
    'content-container': undefined,
    'close-button': {
      height: '24px',
      color: icon.secondary.value,
      'margin-left': '26px',
    },
    'icon-container': undefined,
    'icon-gap': {
      'margin-right': '10px',
    },
    'message-container': {
      'padding-top': `${spacing.XXXS}px`,
      'padding-bottom': `${spacing.XXXS}px`,
    },
    none: {
      'border-left-color': 'transparent',
    },
    info: {
      'border-left-color': border.information.value,
      'background-color': background.information.subtler.value,
      icon: {
        fill: icon.information.value,
      },
    },
    success: {
      'border-left-color': border.success.value,
      'background-color': background.success.subtler.value,
      icon: {
        fill: icon.success.value,
      },
    },
    warning: {
      'border-left-color': border.warning.value,
      'background-color': background.warning.subtler.value,
      icon: {
        fill: icon.warning.value,
      },
    },
    error: {
      'border-left-color': border.error.value,
      'background-color': background.error.subtler.value,
      icon: {
        fill: icon.error.value,
      },
    },
  };
}

type StatusIconFill = {
  'border-left-color'?: string;
  icon?: { fill?: string };
};

type IconSpacingSource = {
  'padding-top'?: string;
  'padding-right'?: string;
  'padding-bottom'?: string;
  'padding-left'?: string;
};

type NotificationToastWeaveSource = Partial<
  Omit<
    NotificationToastDesignTokens,
    'info' | 'success' | 'warning' | 'error' | 'none' | 'box-shadow'
  >
> & {
  'box-shadow-x'?: string;
  'box-shadow-y'?: string;
  'box-shadow-blur'?: string;
  'box-shadow-spread'?: string;
  'box-shadow-color'?: string;
  info?: StatusIconFill;
  success?: StatusIconFill;
  warning?: StatusIconFill;
  error?: StatusIconFill;
  icon?: IconSpacingSource;
};

export function getNotificationTokensWeave(
  tokens: WeaveTheme['tokens'],
): NotificationToastDesignTokens {
  const nt = (
    tokens?.component as
      | { notificationtoast?: NotificationToastWeaveSource }
      | undefined
  )?.notificationtoast;

  const shadowX = nt?.['box-shadow-x'];
  const shadowY = nt?.['box-shadow-y'];
  const shadowBlur = nt?.['box-shadow-blur'];
  const shadowSpread = nt?.['box-shadow-spread'];
  const shadowColor = nt?.['box-shadow-color'];
  const boxShadow =
    shadowX && shadowY && shadowBlur && shadowSpread && shadowColor
      ? `${shadowX} ${shadowY} ${shadowBlur} ${shadowSpread} ${shadowColor}`
      : undefined;

  return {
    'background-color': nt?.['background-color'],
    'border-radius': nt?.['border-radius'],
    'border-left-width': nt?.['border-left-width'],
    'border-left-style': nt?.['border-left-style'],
    'box-shadow': boxShadow,
    'padding-top': nt?.['padding-top'],
    'padding-right': nt?.['padding-right'],
    'padding-bottom': nt?.['padding-bottom'],
    'padding-left': nt?.['padding-left'],
    width: nt?.width,
    'content-container': nt?.['content-container']
      ? {
          ...nt['content-container'],
          /**
           * Weave source has `content-container.padding-right: "0"`. Normalize to `margin-right`
           * (12px in spec) so consumers can read `padding-right` honestly without applying
           * a visually-broken 0.
           */
          'padding-right':
            !nt['content-container']['padding-right'] ||
            nt['content-container']['padding-right'] === '0' ||
            nt['content-container']['padding-right'] === '0px'
              ? nt['content-container']['margin-right']
              : nt['content-container']['padding-right'],
        }
      : undefined,
    'close-button': nt?.['close-button']
      ? {
          ...nt['close-button'],
          color: 'currentColor',
          'margin-left': '0',
          'align-self': 'flex-start',
        }
      : undefined,
    'icon-container': nt?.['icon-container']
      ? {
          ...nt['icon-container'],
          /**
           * Weave source has `icon-container.padding-right: "0"`. Mirror `padding-left` (12px)
           * so consumers can read `padding-right` honestly. Spec is asymmetric; we normalize here.
           */
          'padding-right':
            !nt['icon-container']['padding-right'] ||
            nt['icon-container']['padding-right'] === '0' ||
            nt['icon-container']['padding-right'] === '0px'
              ? nt['icon-container']['padding-left']
              : nt['icon-container']['padding-right'],
        }
      : undefined,
    'icon-gap': nt?.icon
      ? {
          'margin-right': nt.icon['padding-right'],
        }
      : undefined,
    'message-container': nt?.['message-container']
      ? {
          ...nt['message-container'],
          'padding-top': '0',
          'padding-bottom': '0',
        }
      : undefined,
    none: {
      'border-left-color': 'transparent',
      'background-color': undefined,
    },
    info: {
      'border-left-color': nt?.info?.['border-left-color'],
      'background-color': undefined,
      icon: {
        fill: nt?.info?.icon?.fill,
      },
    },
    success: {
      'border-left-color': nt?.success?.['border-left-color'],
      'background-color': undefined,
      icon: {
        fill: nt?.success?.icon?.fill,
      },
    },
    warning: {
      'border-left-color': nt?.warning?.['border-left-color'],
      'background-color': undefined,
      icon: {
        fill: nt?.warning?.icon?.fill,
      },
    },
    error: {
      'border-left-color': nt?.error?.['border-left-color'],
      'background-color': undefined,
      icon: {
        fill: nt?.error?.icon?.fill,
      },
    },
  };
}
